import { useState } from 'react';
import { api } from '../api/client';
import type { RecordInfo } from '../types/models';

/** Format like "31-10-2025 11:15:40 AM". */
function fmt(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  let h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ` +
    `${pad(h)}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${ampm}`;
}

/**
 * An "i" action button that opens a Record Info popup (created/modified by-whom-and-when
 * plus version). `entity` is the audit key (e.g. "institutions", "exams"); `id` is the row id.
 */
export function RecordInfoButton(
  { entity, id, title = 'Record info', className = 'link-btn', label = 'Info' }:
  { entity: string; id: string; title?: string; className?: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState<RecordInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function show() {
    setOpen(true);
    setInfo(null);
    setError(null);
    try {
      setInfo(await api.getRecordInfo(entity, id));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load record info.');
    }
  }

  return (
    <>
      <button type="button" title={title} className={className} onClick={show}>{label}</button>
      {open && (
        <div className="rte-modal-backdrop" onMouseDown={() => setOpen(false)}>
          <div className="rte-modal" style={{ width: 'min(520px, 92vw)' }} onMouseDown={e => e.stopPropagation()}>
            <h3>Record Info</h3>
            {!info && !error && <div className="muted">Loading…</div>}
            {error && <div className="error">{error}</div>}
            {info && (
              <div className="record-info-grid">
                <div className="ri-label">Created Date :</div>
                <div>{fmt(info.createdAt)}</div>
                <div className="ri-label">Created By :</div>
                <div>{info.createdByName ?? <span className="muted">—</span>}</div>
                <div className="ri-label">Modified Date :</div>
                <div>{info.updatedAt ? fmt(info.updatedAt) : <span className="muted">—</span>}</div>
                <div className="ri-label">Modified By :</div>
                <div>{info.updatedByName ?? <span className="muted">—</span>}</div>
                <div className="ri-label">Record Version :</div>
                <div>{info.version}</div>
              </div>
            )}
            <div className="rte-modal-actions">
              <button type="button" className="primary" onClick={() => setOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
